import React, { useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SettingsContext, AuthContext, ClientsContext, ClientGroupsContext } from "@/App";
import { toast } from "@/components/ui/sonner";
import {
  Settings, DollarSign, Clock, Building2, Save, RotateCcw,
  Users, UserPlus, ShieldCheck, ShieldOff, Eye, EyeOff,
  Palette, Trash2, Pencil, Check, X, Plus, KeyRound, Shield, Loader2,
  Mail, Send, FlaskConical, ToggleLeft, ToggleRight, Calendar, ChevronRight,
  SlidersHorizontal, Crown, Megaphone, Database, UserCheck, ImagePlus,
  CheckCircle2, AlertTriangle, Layers,
} from "lucide-react";
import { PRESET_COLORS } from "@/components/CreateClientModal";
import CreateClientModal from "@/components/CreateClientModal";
import UserPermissionsModal from "@/components/UserPermissionsModal";
import BackupSection from "@/components/BackupPanel";
import ClientGroupsPanel from "@/components/admin/ClientGroupsPanel";
import DivisionsPanel from "@/components/admin/DivisionsPanel";
import ClientReportsPanel from "@/components/admin/ClientReportsPanel";
import AiSettingsPanel from "@/components/admin/AiSettingsPanel";
import AnnouncementsPanel from "@/components/admin/AnnouncementsPanel";
import AnalystGlyph from "@/components/analyst/AnalystGlyph";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_COLORS = {
  Admin: { color: "#f97316", bg: "rgba(99,102,241,0.12)" },
  Received: { color: "#3b82f6", bg: "rgba(59,130,246,0.12)" },
  Procurement: { color: "#f97316", bg: "rgba(249,115,22,0.12)" },
  Manufacturing: { color: "#a855f7", bg: "rgba(168,85,247,0.12)" },
  QC: { color: "#14b8a6", bg: "rgba(20,184,166,0.12)" },
  Dispatch: { color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  Stores: { color: "#10b981", bg: "rgba(16,185,129,0.12)" },
  // The module-only roles — each carries its module's own identity colour.
  "Stock Center": { color: "#0ea5e9", bg: "rgba(14,165,233,0.12)" },
  Ecotile: { color: "#16a34a", bg: "rgba(22,163,74,0.12)" },
  Maintenance: { color: "#2F6DAE", bg: "rgba(47,109,174,0.12)" },
  Viewer: { color: "#64748b", bg: "rgba(100,116,139,0.12)" },
};

// Mirrors VALID_ROLES on the server. The last three are MODULE-ONLY roles:
// they have no ERP footing and land straight in their own shell at login
// (see isWarehouseOnly / isEcotileOnly / isMaintenanceOnly in App.js).
const VALID_ROLES = ["Admin", "Received", "Procurement", "Manufacturing", "QC", "Dispatch", "Stores",
                     "Stock Center", "Ecotile", "Maintenance", "Viewer"];

function SettingRow({ icon: Icon, label, description, children }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-[hsl(var(--border))]/50 last:border-0">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: "rgba(99,102,241,0.1)" }}>
        <Icon className="w-4 h-4 text-orange-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[hsl(var(--foreground))]">{label}</p>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{description}</p>
      </div>
      <div className="shrink-0 flex items-center gap-2">{children}</div>
    </div>
  );
}

export default function AdminSettingsPanel({ open, onClose }) {
  const { settings, setSettings, fetchSettings } = useContext(SettingsContext);
  const { user } = useContext(AuthContext);
  const { clients, fetchClients } = useContext(ClientsContext);
  const { clientGroups, fetchClientGroups } = useContext(ClientGroupsContext);
  const [activeTab, setActiveTab] = useState("general");
  const [form, setForm] = useState({ ...settings });
  const [saving, setSaving] = useState(false);

  // User management state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "Received" });
  const [showPassword, setShowPassword] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  // Client management state
  const [editingClientId, setEditingClientId] = useState(null);
  const [editClientName, setEditClientName] = useState("");
  const [showColorPickerFor, setShowColorPickerFor] = useState(null);
  const [savingClientId, setSavingClientId] = useState(null);
  const [deletingClientId, setDeletingClientId] = useState(null);
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [uploadingLogoId, setUploadingLogoId] = useState(null);

  // Password reset state
  const [resetUserId, setResetUserId]     = useState(null);
  const [resetSecret, setResetSecret]     = useState("");
  const [resetNewPwd, setResetNewPwd]     = useState("");
  const [resetConfirm, setResetConfirm]   = useState("");
  const [resetSecretShow, setResetSecretShow] = useState(false);
  const [resetPwdShow, setResetPwdShow]   = useState(false);
  const [resetSaving, setResetSaving]     = useState(false);

  // User edit state (name / email / role)
  const [editUserId, setEditUserId]   = useState(null);
  const [editForm, setEditForm]       = useState({ name: "", email: "", role: "Received" });
  const [editSaving, setEditSaving]   = useState(false);

  // Permissions modal state
  const [permissionsUser, setPermissionsUser] = useState(null);

  // Email tools state
  const [testEmailAddr, setTestEmailAddr]   = useState("");
  const [digestEmailAddr, setDigestEmailAddr] = useState("");
  const [sendingTest, setSendingTest]       = useState(false);
  const [sendingDigest, setSendingDigest]   = useState(false);

  // Daily digest recipients dialog
  const [showRecipientsDialog, setShowRecipientsDialog] = useState(false);
  const [newRecipientEmail, setNewRecipientEmail] = useState("");

  // Daily email budget readout (Email tab)
  const [emailUsage, setEmailUsage] = useState(null);

  // Custom digest configure modal
  const [showCustomDigestModal, setShowCustomDigestModal] = useState(false);
  const [newCustomRecipient, setNewCustomRecipient] = useState("");
  const [sendingCustomDigest, setSendingCustomDigest] = useState(false);
  const [customDigestEmailAddr, setCustomDigestEmailAddr] = useState("");

  useEffect(() => {
    if (open) {
      setForm({ ...settings });
      fetchUsersData();
      fetchClients();
      fetchClientGroups();
    }
  }, [open, settings]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh the daily email counter whenever the Email tab is shown
  useEffect(() => {
    if (open && activeTab === "email") {
      axios.get(`${API}/api/admin/email-usage`)
        .then(r => setEmailUsage(r.data))
        .catch(() => setEmailUsage(null));
    }
  }, [open, activeTab]);

  const fetchUsersData = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${API}/api/users`);
      setUsers(res.data);
    } catch (e) {
      console.error("Failed to fetch users:", e);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const handleChange = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      // The announcement is managed (and saved) on the Announcements tab; leave
      // it out so a stale copy in this form can't overwrite it.
      const rest = { ...form };
      delete rest.announcement;
      await axios.put(`${API}/api/settings`, rest);
      setSettings({ ...form, announcement: settings.announcement });
      await fetchSettings();
      toast.success("Settings saved — changes are now live for all users");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setForm({ ...settings });

  const handleToggleStatus = async (u) => {
    setTogglingId(u.user_id);
    try {
      const res = await axios.patch(`${API}/api/users/${u.user_id}/toggle-status`);
      toast.success(res.data.is_active ? `${u.name} reactivated` : `${u.name} deactivated`);
      await fetchUsersData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update user status");
    } finally {
      setTogglingId(null);
    }
  };

  const handleResetPassword = async () => {
    if (!resetNewPwd || resetNewPwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (resetNewPwd !== resetConfirm) { toast.error("Passwords do not match"); return; }
    setResetSaving(true);
    const storedToken = localStorage.getItem('rl_token');
    try {
      await axios.post(`${API}/api/users/${resetUserId}/reset-password`,
        { secret_key: resetSecret, new_password: resetNewPwd },
        { headers: { Authorization: `Bearer ${storedToken}` } }
      );
      toast.success("Password updated successfully");
      setResetUserId(null); setResetSecret(""); setResetNewPwd(""); setResetConfirm("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reset password");
    } finally {
      setResetSaving(false);
    }
  };

  const handlePermissionsSaved = (userId, newPerms) => {
    setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, permissions: newPerms } : u));
    setPermissionsUser(null);
  };

  const handleSendTest = async () => {
    if (!testEmailAddr) { toast.error("Enter a recipient email address"); return; }
    setSendingTest(true);
    try {
      const res = await axios.post(`${API}/api/email/test`, { to_email: testEmailAddr }, { headers: { Authorization: `Bearer ${localStorage.getItem('rl_token')}` } });
      toast.success(res.data.message || "Test email sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send test email");
    } finally { setSendingTest(false); }
  };

  const handleSendDigest = async () => {
    if (!digestEmailAddr) { toast.error("Enter a recipient email address"); return; }
    setSendingDigest(true);
    try {
      const res = await axios.post(`${API}/api/email/digest`, { to_email: digestEmailAddr }, { headers: { Authorization: `Bearer ${localStorage.getItem('rl_token')}` } });
      toast.success(res.data.message || "Digest email sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send digest email");
    } finally { setSendingDigest(false); }
  };

  const handleSendCustomDigest = async () => {
    if (!customDigestEmailAddr) { toast.error("Enter a recipient email address"); return; }
    setSendingCustomDigest(true);
    try {
      const res = await axios.post(`${API}/api/email/custom-digest`, { to_email: customDigestEmailAddr }, { headers: { Authorization: `Bearer ${localStorage.getItem('rl_token')}` } });
      toast.success(res.data.message || "Custom digest email sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send custom digest email");
    } finally { setSendingCustomDigest(false); }
  };

  // ── Client Management Handlers ──
  const handleStartEditClient = (client) => {
    setEditingClientId(client.id);
    setEditClientName(client.name);
    setShowColorPickerFor(null);
  };

  const handleSaveClientName = async (client) => {
    const newName = editClientName.trim();
    if (!newName || newName === client.name) { setEditingClientId(null); return; }
    setSavingClientId(client.id);
    try {
      await axios.put(`${API}/api/clients/${client.id}`, { name: newName });
      await fetchClients();
      toast.success(`Client renamed to "${newName}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to rename client");
    } finally {
      setSavingClientId(null);
      setEditingClientId(null);
    }
  };

  const handleSaveClientColor = async (client, newColor) => {
    setSavingClientId(client.id);
    try {
      await axios.put(`${API}/api/clients/${client.id}`, { color: newColor });
      await fetchClients();
      toast.success("Color updated");
    } catch (e) {
      toast.error("Failed to update color");
    } finally {
      setSavingClientId(null);
      setShowColorPickerFor(null);
    }
  };

  const handleUploadClientLogo = useCallback(async (client) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".svg";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.name.endsWith(".svg")) { toast.error("Only .svg files are allowed"); return; }
      if (file.size > 50000) { toast.error("SVG file too large (max 50KB)"); return; }
      setUploadingLogoId(client.id);
      try {
        const text = await file.text();
        await axios.put(`${API}/api/clients/${client.id}`, { logo_svg: text });
        await fetchClients();
        toast.success("Logo updated");
      } catch (err) {
        toast.error("Failed to upload logo");
      } finally {
        setUploadingLogoId(null);
      }
    };
    input.click();
  }, [fetchClients]);

  const handleDeleteClient = async (client) => {
    if (!window.confirm(`Delete client "${client.name}"? This cannot be undone.`)) return;
    setDeletingClientId(client.id);
    try {
      await axios.delete(`${API}/api/clients/${client.id}`);
      await fetchClients();
      toast.success(`"${client.name}" deleted`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete client");
    } finally {
      setDeletingClientId(null);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name.trim() || !newUser.email.trim() || !newUser.password.trim()) {
      toast.error("Name, email, and password are required");
      return;
    }
    setCreatingUser(true);
    try {
      await axios.post(`${API}/api/users`, newUser);
      toast.success(`User ${newUser.email} created successfully`);
      setNewUser({ name: "", email: "", password: "", role: "Received" });
      setShowCreateForm(false);
      await fetchUsersData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create user");
    } finally {
      setCreatingUser(false);
    }
  };

  const meIsSuper = !!(users.find(x => x.email === user?.email)?.is_super);

  const handleSaveUserEdit = async (u) => {
    if (!editForm.name.trim() || !editForm.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    const roleChanged = editForm.role !== u.role;
    setEditSaving(true);
    try {
      await axios.put(`${API}/api/users/${u.user_id}`, editForm);
      toast.success(roleChanged
        ? `${editForm.name} updated — permissions reset to ${editForm.role} defaults`
        : `${editForm.name} updated`);
      setEditUserId(null);
      await fetchUsersData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update user");
    } finally {
      setEditSaving(false);
    }
  };

  const usdPreview = form.inr_rate ? (1 / form.inr_rate).toFixed(4) : "—";
  const inrPreview = Number(form.inr_rate || 95).toFixed(2);

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-4xl w-full p-0 overflow-hidden"
        style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}
        data-testid="admin-settings-panel"
      >
        {/* Top accent */}
        <div className="h-1 w-full bg-gradient-to-r from-orange-500 via-purple-500 to-blue-500 shrink-0" />

        <div className="px-6 pt-5 shrink-0">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2.5" style={{ fontFamily: "var(--rl-font-heading), sans-serif" }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #f97316, #8b5cf6)" }}>
                <Settings className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-base font-bold text-[hsl(var(--foreground))]">Admin Settings</p>
                <p className="text-xs font-normal text-[hsl(var(--muted-foreground))]">Global configuration · Changes affect all users immediately</p>
              </div>
            </DialogTitle>
          </DialogHeader>
        </div>

        {/* Tab bar */}
        <div className="flex items-center px-6 border-b border-[hsl(var(--border))]/60 shrink-0 bg-[hsl(var(--card))] overflow-x-auto">
          {[
            { id: "general",   label: "General",   icon: Settings },
            { id: "clients",   label: "Clients",   icon: Users },
            { id: "divisions", label: "Divisions", icon: Layers },
            { id: "users",     label: "Users",     icon: UserCheck },
            { id: "email",     label: "Email",     icon: Mail },
            { id: "announcements", label: "Announcements", icon: Megaphone },
            { id: "backup",    label: "Backup",    icon: Database },
            { id: "ai",        label: "Analyst",   icon: AnalystGlyph },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              data-testid={`admin-tab-${id}`}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold border-b-2 -mb-px transition-colors shrink-0 whitespace-nowrap ${
                activeTab === id
                  ? "border-orange-500 text-orange-500"
                  : "border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* Scrollable body */}
        <div className="px-6 py-3 overflow-y-auto flex-1">

          {/* ── General ── */}
          {activeTab === "general" && (<>

          {/* ── Section: Currency ── */}
          <div className="mb-1 mt-3">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Currency</p>
          </div>

          <SettingRow icon={DollarSign} label="USD → INR Exchange Rate" description={`Currently: $1.00 = ₹${inrPreview} · Used in all spend modals across the app`}>
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono-data text-[hsl(var(--muted-foreground))]">₹</span>
              <input
                type="number" min="1" max="999" step="0.1"
                value={form.inr_rate ?? 95}
                onChange={e => handleChange("inr_rate", parseFloat(e.target.value) || 95)}
                className="w-20 h-8 px-2 text-sm font-mono-data rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500 text-right"
                data-testid="settings-inr-rate"
              />
              <span className="text-xs text-[hsl(var(--muted-foreground))]">per $1</span>
            </div>
          </SettingRow>

          <div className="my-2 flex items-center gap-3 px-4 py-2.5 rounded-lg" style={{ background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.15)" }}>
            <span className="text-xl font-extrabold font-mono-data text-orange-400">$1.00</span>
            <span className="text-[hsl(var(--muted-foreground))] text-sm">=</span>
            <span className="text-xl font-extrabold font-mono-data text-orange-400">₹{inrPreview}</span>
            <span className="ml-auto text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Live Preview</span>
          </div>

          {/* ── Section: Stock Center currency rates (→ USD) ── */}
          <div className="mb-1 mt-4">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Stock Center — Currency Rates</p>
          </div>
          <div className="px-4 py-3 rounded-lg bg-[hsl(var(--muted))]/30 border border-[hsl(var(--border))]/60 mb-2">
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-3 leading-relaxed">
              Per-currency → USD rates, used to convert a Stock Center sale into the ERP's USD value. The rate is stored on each sale, so changing it here never alters past sales. Set these before enabling the ERP sale mirror.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {["EUR", "AUD", "CAD", "CHF", "INR"].map((ccy) => (
                <div key={ccy} className="flex items-center gap-2">
                  <span className="text-[11px] font-mono-data font-bold text-[hsl(var(--muted-foreground))] w-9 shrink-0">{ccy}</span>
                  <input
                    type="number" min="0" step="0.0001"
                    value={form.fx_rates?.[ccy] ?? ""}
                    onChange={e => handleChange("fx_rates", { ...(form.fx_rates || {}), [ccy]: parseFloat(e.target.value) || 0 })}
                    className="flex-1 min-w-0 h-8 px-2 text-sm font-mono-data rounded-md bg-[hsl(var(--background))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-emerald-500 text-right"
                    data-testid={`settings-fx-${ccy}`}
                  />
                  <span className="text-[10px] text-[hsl(var(--muted-foreground))] shrink-0">USD</span>
                </div>
              ))}
            </div>
            {form.fx_rates_updated_at && (
              <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-2.5">Last saved: {new Date(form.fx_rates_updated_at).toLocaleString()}</p>
            )}
          </div>

          {/* ── Section: Stock Center ERP sale mirror ── */}
          <div className="mb-1 mt-4">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Stock Center — ERP Sale Mirror</p>
          </div>
          <div className="px-4 py-3 rounded-lg bg-[hsl(var(--muted))]/30 border border-[hsl(var(--border))]/60 mb-2">
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-3 leading-relaxed">
              When enabled, a Stock Center sale also creates a sale in the main ERP under the chosen client group, converted to USD via the rates above. Off by default. The mirror writes to live ERP sales, so set the client group and confirm the rates before enabling.
            </p>
            <div className="mb-3">
              <label className="text-[11px] font-medium text-[hsl(var(--foreground))] block mb-1.5">Target client group</label>
              <select
                value={form.sc_mirror_client_group ?? ""}
                onChange={e => handleChange("sc_mirror_client_group", e.target.value)}
                className="w-full h-9 px-2 text-sm rounded-md bg-[hsl(var(--background))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="settings-sc-mirror-group"
              >
                <option value="">— Select a client group —</option>
                {(clientGroups || []).map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => handleChange("sc_mirror_enabled", !form.sc_mirror_enabled)}
              disabled={!form.sc_mirror_client_group}
              data-testid="settings-sc-mirror-toggle"
              className={`flex items-center gap-3 w-full p-2.5 rounded-lg border text-left transition-all ${
                form.sc_mirror_enabled
                  ? "border-emerald-500/40 bg-emerald-500/8"
                  : "border-[hsl(var(--border))]/60 bg-[hsl(var(--muted))]/20 hover:bg-[hsl(var(--muted))]/40"
              } ${!form.sc_mirror_client_group ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <div className={`w-9 h-5 rounded-full transition-colors shrink-0 ${form.sc_mirror_enabled ? "bg-emerald-600" : "bg-[hsl(var(--muted-foreground))]/30"}`}>
                <div className={`w-3.5 h-3.5 bg-white rounded-full mt-[3px] transition-transform shadow-sm ${form.sc_mirror_enabled ? "translate-x-[18px]" : "translate-x-[3px]"}`} />
              </div>
              <div>
                <p className="text-sm font-medium leading-none mb-0.5">Mirror sales into the ERP</p>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))]">
                  {!form.sc_mirror_client_group
                    ? "Select a client group first"
                    : form.sc_mirror_enabled ? "Active — sales mirror into the ERP" : "Inactive — sales stay in the Stock Center only"}
                </p>
              </div>
            </button>
            <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-2">Note: the mirror engine ships in a later update; this configures it ahead of time.</p>
          </div>

          {/* ── Section: Pipeline ── */}
          <div className="mb-1 mt-4">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Pipeline Alerts</p>
          </div>

          <SettingRow icon={Clock} label='"Due This Week" Threshold' description="Days ahead to trigger the amber alert banner on Stage Pages">
            <div className="flex items-center gap-2">
              <input
                type="number" min="1" max="30" step="1"
                value={form.due_this_week_days ?? 7}
                onChange={e => handleChange("due_this_week_days", parseInt(e.target.value) || 7)}
                className="w-16 h-8 px-2 text-sm font-mono-data rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500 text-right"
                data-testid="settings-due-days"
              />
              <span className="text-xs text-[hsl(var(--muted-foreground))]">days</span>
            </div>
          </SettingRow>

          <SettingRow icon={Clock} label="Default Items Per Page" description="Initial pagination size shown on all Stage Pages for new sessions">
            <select
              value={form.default_page_size ?? 100}
              onChange={e => handleChange("default_page_size", parseInt(e.target.value))}
              className="h-8 px-2.5 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="settings-default-page-size"
            >
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
              <option value={200}>200 / page</option>
              <option value={0}>All items</option>
            </select>
          </SettingRow>

          {/* ── Section: Company ── */}
          <div className="mb-1 mt-4">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Company Identity</p>
          </div>

          <SettingRow icon={Building2} label="Company Name" description="Displayed in login page footer and export headers">
            <input
              type="text"
              value={form.company_name ?? ""}
              onChange={e => handleChange("company_name", e.target.value)}
              className="w-48 h-8 px-2.5 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="settings-company-name"
            />
          </SettingRow>

          <SettingRow icon={Building2} label="Company Location" description="City and country shown in the login page">
            <input
              type="text"
              value={form.company_location ?? ""}
              onChange={e => handleChange("company_location", e.target.value)}
              className="w-48 h-8 px-2.5 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="settings-company-location"
            />
          </SettingRow>

          {settings.updated_by && (
            <p className="text-[10px] text-[hsl(var(--muted-foreground))] pt-2 pb-1">
              Last updated by <span className="font-semibold">{settings.updated_by}</span>
              {settings.updated_at && ` · ${new Date(settings.updated_at).toLocaleString()}`}
            </p>
          )}

          </>)}

          {/* ── Clients ── */}
          {activeTab === "clients" && (<>

          {/* ── Section: Clients ── */}
          <div className="mt-4 mb-1.5 flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Client Management</p>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{clients.length} clients</span>
              <button
                onClick={() => setShowCreateClient(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                style={{ background: "rgba(99,102,241,0.08)", color: "#f97316", border: "1px solid rgba(99,102,241,0.2)" }}
                data-testid="add-client-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Add Client
              </button>
            </div>
          </div>

          <div className="space-y-1.5 mb-3" data-testid="clients-management-list">
            {clients.map(client => (
              <div key={client.id} className="flex items-center gap-3 p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
                {/* Color swatch button */}
                <div className="relative shrink-0">
                  <button
                    onClick={() => setShowColorPickerFor(prev => prev === client.id ? null : client.id)}
                    className="w-8 h-8 rounded-full border-2 border-white shadow-sm transition-transform hover:scale-110"
                    style={{ backgroundColor: client.color }}
                    title="Edit color"
                    data-testid={`client-color-btn-${client.id}`}
                  >
                    <Palette className="w-3.5 h-3.5 text-white drop-shadow mx-auto" style={{ opacity: 0.8 }} />
                  </button>
                  {/* Inline color picker */}
                  {showColorPickerFor === client.id && (
                    <div
                      className="absolute left-0 top-10 z-50 p-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-xl"
                      style={{ minWidth: "170px" }}
                    >
                      <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2">Pick a color</p>
                      <div className="flex flex-wrap gap-1.5">
                        {PRESET_COLORS.map(c => (
                          <button
                            key={c}
                            onClick={() => handleSaveClientColor(client, c)}
                            className="w-6 h-6 rounded-full border-2 transition-all hover:scale-110"
                            style={{
                              backgroundColor: c,
                              borderColor: client.color === c ? "#fff" : "transparent",
                              boxShadow: client.color === c ? `0 0 0 2px ${c}` : "none",
                            }}
                            data-testid={`client-color-pick-${c.replace("#","")}`}
                          />
                        ))}
                      </div>
                      {/* Hex input + native picker */}
                      <div className="flex items-center gap-1.5 mt-2.5 pt-2.5 border-t border-[hsl(var(--border))]/50">
                        <input
                          type="text"
                          placeholder="#hex"
                          defaultValue={client.color}
                          key={client.color}
                          maxLength={7}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              const val = e.target.value.trim();
                              if (/^#[0-9a-fA-F]{6}$/.test(val)) {
                                handleSaveClientColor(client, val);
                              }
                            }
                          }}
                          className="w-[72px] h-7 px-2 text-xs font-mono-data rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 focus:outline-none focus:ring-1 focus:ring-orange-500"
                        />
                        <label className="relative cursor-pointer">
                          <input
                            type="color"
                            value={client.color}
                            onChange={(e) => handleSaveClientColor(client, e.target.value)}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          />
                          <div
                            className="w-7 h-7 rounded-lg border-2 border-[hsl(var(--border))] hover:scale-110 transition-transform flex items-center justify-center"
                            style={{ backgroundColor: client.color }}
                            title="Pick from screen"
                          >
                            <Palette className="w-3 h-3 text-white drop-shadow" style={{ opacity: 0.7 }} />
                          </div>
                        </label>
                      </div>
                      <button
                        onClick={() => setShowColorPickerFor(null)}
                        className="mt-2 text-[10px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>

                {/* Name — editable */}
                <div className="flex-1 min-w-0">
                  {editingClientId === client.id ? (
                    <div className="flex items-center gap-1.5">
                      <input
                        type="text"
                        value={editClientName}
                        onChange={e => setEditClientName(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") handleSaveClientName(client); if (e.key === "Escape") setEditingClientId(null); }}
                        className="flex-1 h-7 px-2 text-sm rounded bg-[hsl(var(--muted))] border border-[hsl(var(--border))] focus:outline-none focus:ring-1 focus:ring-orange-500"
                        autoFocus
                        data-testid={`client-name-edit-${client.id}`}
                      />
                      <button onClick={() => handleSaveClientName(client)} className="p-1 rounded text-green-500 hover:bg-green-500/10" disabled={savingClientId === client.id} data-testid={`client-name-save-${client.id}`}>
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setEditingClientId(null)} className="p-1 rounded text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span
                        className="text-sm font-semibold px-2 py-0.5 rounded"
                        style={{ color: client.color, backgroundColor: client.color + "18" }}
                      >
                        {client.name}
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleUploadClientLogo(client)}
                    disabled={uploadingLogoId === client.id}
                    className="p-1.5 rounded hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-orange-500 transition-colors disabled:opacity-40"
                    title={client.logo_svg ? "Replace logo" : "Upload SVG logo"}
                    data-testid={`client-logo-btn-${client.id}`}
                  >
                    {client.logo_svg ? (
                      <span
                        className="flex items-center justify-center w-3.5 h-3.5"
                        dangerouslySetInnerHTML={{ __html: client.logo_svg }}
                      />
                    ) : (
                      <ImagePlus className="w-3.5 h-3.5" style={{ opacity: 0.5 }} />
                    )}
                  </button>
                  <button
                    onClick={() => handleStartEditClient(client)}
                    className="p-1.5 rounded hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-orange-500 transition-colors"
                    title="Rename client"
                    data-testid={`client-edit-btn-${client.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDeleteClient(client)}
                    disabled={deletingClientId === client.id}
                    className="p-1.5 rounded hover:bg-red-500/10 text-[hsl(var(--muted-foreground))] hover:text-red-500 transition-colors disabled:opacity-40"
                    title="Delete client (only if no items)"
                    data-testid={`client-delete-btn-${client.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* ── Section: Client Groups ── */}
          <ClientGroupsPanel />

          </>)}

          {/* ── Divisions ── */}
          {activeTab === "divisions" && (
            <DivisionsPanel />
          )}

          {/* ── Email ── */}
          {activeTab === "email" && (<>

          {/* ── Section: Email ── */}
          <div className="mt-4 mb-2">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Email Configuration</p>
          </div>

          {/* Connection & sender health strip */}
          {emailUsage && (emailUsage.configured ? (
            <div className="rl-glass-card p-4 rounded-xl border border-emerald-500/30 mb-3" data-testid="email-health-strip">
              <div className="flex items-center gap-2 mb-2.5">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <span className="text-sm font-semibold text-emerald-700">Email service connected</span>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-[hsl(var(--muted-foreground))]">Sending as</span>
                  <span className="font-mono-data text-[hsl(var(--foreground))]">{emailUsage.sender_email || "—"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[hsl(var(--muted-foreground))]">Domain</span>
                  <span className="flex items-center gap-1">
                    <span className="font-mono-data text-[hsl(var(--foreground))]">{emailUsage.sender_domain || "—"}</span>
                    {emailUsage.sender_domain && <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] mb-3" data-testid="email-health-strip">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <span className="text-sm font-semibold text-amber-700">Email service not configured</span>
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">No emails will send until the server's email key is set. Digests, sign-in codes, and mention alerts are paused.</p>
            </div>
          ))}

          {/* Notifications toggle */}
          <div className="rl-glass-card p-4 rounded-xl border border-[hsl(var(--border))] mb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold">Stage Transition Emails</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">Send an email to admin + stage contact whenever an item advances or is sent back</p>
              </div>
              <button
                onClick={() => handleChange("email_notifications_enabled", !form.email_notifications_enabled)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${form.email_notifications_enabled ? 'bg-emerald-500/15 text-emerald-600 border border-emerald-500/30' : 'bg-[hsl(var(--muted))]/40 text-[hsl(var(--muted-foreground))] border border-[hsl(var(--border))]'}`}
                data-testid="email-notifications-toggle"
              >
                {form.email_notifications_enabled
                  ? <><ToggleRight className="w-4 h-4" /> Enabled</>
                  : <><ToggleLeft className="w-4 h-4" /> Disabled</>
                }
              </button>
            </div>
          </div>

          {/* Per-stage notification emails */}
          {form.email_notifications_enabled && (
            <div className="p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 mb-2 space-y-1.5">
              <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-2">Notification Recipients Per Stage</p>
              {["PO Received","Procurement","Manufacturing","Quality Control","Dispatch","Completed"].map(stage => (
                <div key={stage} className="flex items-center gap-2">
                  <span className="text-xs w-32 shrink-0 text-[hsl(var(--muted-foreground))]">{stage}</span>
                  <input
                    type="email"
                    value={(form.stage_notification_emails || {})[stage] || ""}
                    onChange={e => handleChange("stage_notification_emails", { ...(form.stage_notification_emails || {}), [stage]: e.target.value })}
                    placeholder="role@company.com"
                    className="flex-1 h-7 px-2 text-xs rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-1 focus:ring-orange-500"
                    data-testid={`stage-email-${stage.replace(/\s/g,'-').toLowerCase()}`}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Mention email notifications */}
          <div className="rl-glass-card p-4 rounded-xl border border-[hsl(var(--border))] mb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold">Mention Emails</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">Email users when they're @mentioned, tagged on a blocked item, or a client request needs attention — with a link that opens the right page pre-searched</p>
              </div>
              <button
                onClick={() => handleChange("mention_emails_enabled", !form.mention_emails_enabled)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${form.mention_emails_enabled ? 'bg-emerald-500/15 text-emerald-600 border border-emerald-500/30' : 'bg-[hsl(var(--muted))]/40 text-[hsl(var(--muted-foreground))] border border-[hsl(var(--border))]'}`}
                data-testid="mention-emails-toggle"
              >
                {form.mention_emails_enabled
                  ? <><ToggleRight className="w-4 h-4" /> Enabled</>
                  : <><ToggleLeft className="w-4 h-4" /> Disabled</>
                }
              </button>
            </div>
            {/* Always visible: Client Reports' logos + links need this URL
                too, so hiding it behind the Mention Emails switch made it
                unfindable (23-Oct-2026). */}
            {(
              <div className="mt-3 pt-3 border-t border-[hsl(var(--border))]/50">
                <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold block mb-1">App URL for email links</label>
                <input
                  type="url"
                  value={form.app_base_url || ""}
                  onChange={e => handleChange("app_base_url", e.target.value)}
                  placeholder="https://your-app-url.com"
                  className="w-full h-8 px-2.5 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono-data"
                  data-testid="app-base-url-input"
                />
                {!(form.app_base_url || "").trim() ? (
                  <p className="text-[11px] text-amber-600 mt-1.5">Required — without it no mention or ETA-alert emails are sent, and client reports lose their logo and links.</p>
                ) : (
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1.5">Used for links in mention, ETA-alert and client-report emails. Production: https://rocklink.rockpecker.com</p>
                )}
              </div>
            )}
          </div>

          {/* Email usage — daily cap (RockLink's own) + monthly (provider ceiling) */}
          <div className="rl-glass-card p-4 rounded-xl border border-[hsl(var(--border))] mb-3">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-sm font-semibold">Email Usage</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">Counts ALL outbound email — digests, sign-in codes, mentions. 0 = unlimited daily cap.</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">Daily cap</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.email_daily_limit ?? 80}
                  onChange={e => handleChange("email_daily_limit", Math.max(0, parseInt(e.target.value || "0", 10)))}
                  className="w-16 h-8 px-2.5 text-sm text-right rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono-data"
                  data-testid="email-daily-limit-input"
                />
              </div>
            </div>
            {emailUsage && (() => {
              const dCap = emailUsage.provider_daily_cap || 100;
              const mCap = emailUsage.provider_monthly_cap || 3000;
              const dPct = Math.min(100, Math.round((emailUsage.sent / dCap) * 100));
              const mPct = Math.min(100, Math.round(((emailUsage.sent_month || 0) / mCap) * 100));
              const overLimit = emailUsage.limit && emailUsage.sent >= emailUsage.limit;
              return (
                <div className="space-y-2.5">
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-[hsl(var(--muted-foreground))]">Today</span>
                      <span className="font-mono-data" data-testid="email-usage-today">
                        <span className={overLimit ? 'text-red-500 font-bold' : 'text-[hsl(var(--foreground))]'}>{emailUsage.sent}</span>
                        <span className="text-[hsl(var(--muted-foreground))]"> / {emailUsage.limit || '∞'} · {dCap}/day service limit</span>
                      </span>
                    </div>
                    <div className="rl-track h-2 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                      <div className={`rl-track-fill h-full rounded-full ${dPct >= 90 ? 'bg-red-500' : dPct >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${dPct}%` }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-[hsl(var(--muted-foreground))]">This month</span>
                      <span className="font-mono-data" data-testid="email-usage-month">
                        <span className="text-[hsl(var(--foreground))]">{emailUsage.sent_month || 0}</span>
                        <span className="text-[hsl(var(--muted-foreground))]"> / {mCap.toLocaleString()} service cap</span>
                      </span>
                    </div>
                    <div className="rl-track h-2 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                      <div className={`rl-track-fill h-full rounded-full ${mPct >= 90 ? 'bg-red-500' : mPct >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${mPct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Owner Digest (full pipeline view) */}
          <div className="rl-glass-card p-4 rounded-xl border border-[hsl(var(--border))] mb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold flex items-center gap-2">
                  <Crown className="w-4 h-4 text-amber-400" />
                  Owner Digest
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 border border-amber-500/20">Full View</span>
                </p>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">Full pipeline snapshot at 12:00 PM IST — all stages, financials & alerts</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowRecipientsDialog(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                  style={{ background: "rgba(168,85,247,0.08)", color: "#a855f7", border: "1px solid rgba(168,85,247,0.2)" }}
                  data-testid="manage-digest-recipients-btn"
                >
                  <Users className="w-3.5 h-3.5" />
                  {(form.digest_management_emails || []).length} recipient{(form.digest_management_emails || []).length !== 1 ? "s" : ""}
                  <ChevronRight className="w-3 h-3" />
                </button>
                <button
                  onClick={() => handleChange("daily_digest_enabled", !form.daily_digest_enabled)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${form.daily_digest_enabled ? 'bg-purple-500/15 text-purple-600 border border-purple-500/30' : 'bg-[hsl(var(--muted))]/40 text-[hsl(var(--muted-foreground))] border border-[hsl(var(--border))]'}`}
                  data-testid="daily-digest-toggle"
                >
                  {form.daily_digest_enabled
                    ? <><ToggleRight className="w-4 h-4" /> Enabled</>
                    : <><ToggleLeft className="w-4 h-4" /> Disabled</>
                  }
                </button>
              </div>
            </div>
          </div>

          {/* Custom Digest */}
          <div className="rl-glass-card p-4 rounded-xl border border-[hsl(var(--border))] mb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold flex items-center gap-2">
                  <SlidersHorizontal className="w-4 h-4 text-orange-400" />
                  Custom Digest
                  {(form.custom_digest?.enabled) && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-600 border border-orange-500/20">Active</span>
                  )}
                </p>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                  Tailored pipeline report — choose which stages and data to include
                  {(form.custom_digest?.visible_stages || []).length > 0 && (form.custom_digest?.visible_stages || []).length < 6 && (
                    <span className="ml-1.5 text-orange-400">{(form.custom_digest?.visible_stages || []).length} stage{(form.custom_digest?.visible_stages || []).length !== 1 ? "s" : ""} selected</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowCustomDigestModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                  style={{ background: "rgba(99,102,241,0.08)", color: "#f97316", border: "1px solid rgba(99,102,241,0.2)" }}
                  data-testid="configure-custom-digest-btn"
                >
                  <SlidersHorizontal className="w-3.5 h-3.5" />
                  Configure
                </button>
                <button
                  onClick={() => handleChange("custom_digest", { ...(form.custom_digest || {}), enabled: !(form.custom_digest?.enabled) })}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${form.custom_digest?.enabled ? 'bg-orange-500/15 text-orange-600 border border-orange-500/30' : 'bg-[hsl(var(--muted))]/40 text-[hsl(var(--muted-foreground))] border border-[hsl(var(--border))]'}`}
                  data-testid="custom-digest-toggle"
                >
                  {form.custom_digest?.enabled
                    ? <><ToggleRight className="w-4 h-4" /> Enabled</>
                    : <><ToggleLeft className="w-4 h-4" /> Disabled</>
                  }
                </button>
              </div>
            </div>
          </div>

          {/* Client Reports — daily per-client / per-group movement report */}
          <ClientReportsPanel
            reports={form.client_reports || []}
            onChange={(list) => handleChange("client_reports", list)}
            savedIds={(settings?.client_reports || []).map(r => r.id)}
            clients={clients}
            clientGroups={clientGroups}
          />

          {/* Email Tools */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            {/* Test Email */}
            <div className="rl-glass-card p-3 rounded-xl border border-[hsl(var(--border))] space-y-2">
              <div className="flex items-center gap-2 mb-1">
                <FlaskConical className="w-3.5 h-3.5 text-orange-400" />
                <p className="text-xs font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Test Email</p>
              </div>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Send a test email to verify the email service is working</p>
              <input
                type="email"
                value={testEmailAddr}
                onChange={e => setTestEmailAddr(e.target.value)}
                placeholder="your@email.com"
                className="w-full h-7 px-2 text-xs rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-1 focus:ring-orange-500"
                data-testid="test-email-input"
              />
              <button
                onClick={handleSendTest}
                disabled={sendingTest || !testEmailAddr}
                className="rl-lit-btn w-full flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold rounded-full bg-orange-600 hover:bg-orange-700 text-white transition-colors disabled:opacity-50"
                data-testid="send-test-email-btn"
              >
                {sendingTest ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                {sendingTest ? "Sending..." : "Send Test"}
              </button>
            </div>

            {/* Report Digest */}
            <div className="rl-glass-card p-3 rounded-xl border border-[hsl(var(--border))] space-y-2">
              <div className="flex items-center gap-2 mb-1">
                <Mail className="w-3.5 h-3.5 text-purple-400" />
                <p className="text-xs font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Report Digest</p>
              </div>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Send a full pipeline report: blocked, overdue & stuck items</p>
              <input
                type="email"
                value={digestEmailAddr}
                onChange={e => setDigestEmailAddr(e.target.value)}
                placeholder="your@email.com"
                className="w-full h-7 px-2 text-xs rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-1 focus:ring-orange-500"
                data-testid="digest-email-input"
              />
              <button
                onClick={handleSendDigest}
                disabled={sendingDigest || !digestEmailAddr}
                className="rl-lit-btn w-full flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold rounded-full bg-purple-600 hover:bg-purple-700 text-white transition-colors disabled:opacity-50"
                data-testid="send-digest-btn"
              >
                {sendingDigest ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                {sendingDigest ? "Sending..." : "Send Digest"}
              </button>
            </div>
          </div>

          {/* Technical footnote — provider name kept out of the main UI on purpose */}
          <p className="text-[10px] text-[hsl(var(--muted-foreground))]/60 text-center mb-2">Powered by Resend</p>

          </>)}

          {/* ── Backup ── */}
          {activeTab === "ai" && <AiSettingsPanel />}

          {activeTab === "announcements" && <AnnouncementsPanel />}

          {activeTab === "backup" && (
            <BackupSection />
          )}

          {/* ── Users ── */}
          {activeTab === "users" && (<>

          {/* ── Section: User Management ── */}
          <div className="mt-2 mb-1.5 flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">User Management</p>
            <button
              onClick={() => setShowCreateForm(v => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
              style={{ background: showCreateForm ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.08)", color: "#f97316", border: "1px solid rgba(99,102,241,0.2)" }}
              data-testid="create-user-toggle-btn"
            >
              <UserPlus className="w-3.5 h-3.5" /> {showCreateForm ? "Cancel" : "Add User"}
            </button>
          </div>

          {/* Create User Form */}
          {showCreateForm && (
            <div className="mb-3 p-3 rounded-lg border border-orange-500/20 bg-[hsl(var(--muted))]/30 space-y-2" data-testid="create-user-form">
              <p className="text-xs font-bold text-[hsl(var(--foreground))] uppercase tracking-wider">New User</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold block mb-1">Full Name</label>
                  <input
                    type="text"
                    value={newUser.name}
                    onChange={e => setNewUser(u => ({ ...u, name: e.target.value }))}
                    placeholder="e.g. John Smith"
                    className="w-full h-8 px-2.5 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid="new-user-name"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold block mb-1">Role</label>
                  <select
                    value={newUser.role}
                    onChange={e => setNewUser(u => ({ ...u, role: e.target.value }))}
                    className="w-full h-8 px-2.5 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid="new-user-role"
                  >
                    {VALID_ROLES.map(r => <option key={r} value={r} disabled={r === "Admin" && !meIsSuper}>{r}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold block mb-1">Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={e => setNewUser(u => ({ ...u, email: e.target.value }))}
                  placeholder="user@rockpecker.com"
                  className="w-full h-8 px-2.5 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="new-user-email"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold block mb-1">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newUser.password}
                    onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))}
                    placeholder="Min. 6 characters"
                    className="w-full h-8 pr-9 pl-2.5 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid="new-user-password"
                  />
                  <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
                    {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              <div className="flex justify-end pt-1">
                <button
                  onClick={handleCreateUser}
                  disabled={creatingUser}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white transition-colors disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg, #f97316, #8b5cf6)" }}
                  data-testid="create-user-submit-btn"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  {creatingUser ? "Creating..." : "Create User"}
                </button>
              </div>
            </div>
          )}

          {/* Users List */}
          <div className="space-y-1.5 pb-3" data-testid="users-list">
            {loadingUsers ? (
              <div className="flex items-center justify-center py-6 text-[hsl(var(--muted-foreground))]">
                <div className="w-5 h-5 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin mr-2" />
                <span className="text-sm">Loading users...</span>
              </div>
            ) : (
              users.map(u => {
                const rc = ROLE_COLORS[u.role] || { color: "#94a3b8", bg: "rgba(148,163,184,0.12)" };
                const isCurrentUser = u.email === user?.email;
                const isActive = u.is_active !== false;
                const isResetting = resetUserId === u.user_id;
                const isEditing = editUserId === u.user_id;
                const isProtected = !!u.is_super && !isCurrentUser;
                return (
                  <div key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                    {/* Main user row */}
                    <div className={`flex flex-col items-start gap-2 md:flex-row md:items-center md:justify-between p-3 rounded-lg border transition-opacity ${isActive ? 'border-[hsl(var(--border))] bg-[hsl(var(--card))]' : 'border-[hsl(var(--border))]/50 bg-[hsl(var(--muted))]/30 opacity-60'} ${(isResetting || isEditing) ? 'rounded-b-none border-b-0' : ''}`}>
                      <div className="flex items-center gap-3 min-w-0 w-full md:w-auto">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ backgroundColor: rc.color }}>
                          {(u.name || u.email).charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-[hsl(var(--foreground))]">{u.name}</span>
                            {isCurrentUser && <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-500 font-bold">You</span>}
                            {!isActive && <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-500/15 text-zinc-400 font-bold">Deactivated</span>}
                          </div>
                          <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap shrink-0">
                        {u.is_super && (
                          <span title="Super admin — protected account" className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-orange-500 bg-orange-500/10" data-testid={`super-badge-${u.user_id}`}>
                            <Crown className="w-3 h-3" /> Super
                          </span>
                        )}
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ color: rc.color, backgroundColor: rc.bg }}>{u.role}</span>
                        {isProtected ? (
                          <span className="text-[10px] text-[hsl(var(--muted-foreground))] px-1.5">Protected</span>
                        ) : (<>
                        {/* Edit user button */}
                        <button
                          onClick={() => {
                            if (isEditing) { setEditUserId(null); return; }
                            setEditForm({ name: u.name || "", email: u.email || "", role: u.role });
                            setEditUserId(u.user_id);
                            setResetUserId(null);
                          }}
                          title="Edit user"
                          className={`p-1.5 rounded transition-colors ${isEditing ? 'bg-blue-500/15 text-blue-500' : 'hover:bg-blue-500/10 text-[hsl(var(--muted-foreground))] hover:text-blue-500'}`}
                          data-testid={`edit-user-btn-${u.user_id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        {/* Permissions button */}
                        <button
                          onClick={() => setPermissionsUser(u)}
                          title="Edit permissions"
                          className="p-1.5 rounded hover:bg-orange-500/10 text-[hsl(var(--muted-foreground))] hover:text-orange-500 transition-colors"
                          data-testid={`permissions-btn-${u.user_id}`}
                        >
                          <Shield className="w-3.5 h-3.5" />
                        </button>
                        {/* Password reset button */}
                        <button
                          onClick={() => {
                            setResetUserId(isResetting ? null : u.user_id);
                            setResetSecret(""); setResetNewPwd(""); setResetConfirm("");
                            setEditUserId(null);
                          }}
                          title="Reset password"
                          className={`p-1.5 rounded transition-colors ${isResetting ? 'bg-amber-500/15 text-amber-500' : 'hover:bg-amber-500/10 text-[hsl(var(--muted-foreground))] hover:text-amber-500'}`}
                          data-testid={`reset-pwd-btn-${u.user_id}`}
                        >
                          <KeyRound className="w-3.5 h-3.5" />
                        </button>
                        {!isCurrentUser && (
                          <button
                            onClick={() => handleToggleStatus(u)}
                            disabled={togglingId === u.user_id}
                            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 ${isActive ? 'bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50' : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50'}`}
                            data-testid={`toggle-user-${u.user_id}`}
                          >
                            {togglingId === u.user_id ? (
                              <span className="w-3 h-3 border border-current/30 border-t-current rounded-full animate-spin" />
                            ) : isActive ? (
                              <><ShieldOff className="w-3 h-3" /> Deactivate</>
                            ) : (
                              <><ShieldCheck className="w-3 h-3" /> Reactivate</>
                            )}
                          </button>
                        )}
                        </>)}
                      </div>
                    </div>
                    {/* Inline user edit form */}
                    {isEditing && (
                      <div className="border border-blue-500/30 border-t-0 rounded-b-lg bg-blue-500/5 px-3 py-2 space-y-1.5" data-testid={`edit-form-${u.user_id}`}>
                        <p className="text-[10px] uppercase tracking-widest font-bold text-blue-600 mb-2">Edit User — {u.name}</p>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold block mb-1">Full Name</label>
                            <input
                              type="text"
                              value={editForm.name}
                              onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                              className="w-full h-8 px-2.5 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-blue-500"
                              data-testid="edit-user-name"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold block mb-1">Email</label>
                            <input
                              type="email"
                              value={editForm.email}
                              onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                              className="w-full h-8 px-2.5 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-blue-500"
                              data-testid="edit-user-email"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold block mb-1">Role</label>
                            <select
                              value={editForm.role}
                              onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))}
                              disabled={isCurrentUser}
                              title={isCurrentUser ? "You cannot change your own role" : undefined}
                              className="w-full h-8 px-2.5 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                              data-testid="edit-user-role"
                            >
                              {VALID_ROLES.map(r => (
                                <option key={r} value={r} disabled={r === "Admin" ? (!meIsSuper && u.role !== "Admin") : (u.role === "Admin" && !meIsSuper)}>{r}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="flex items-center justify-between pt-1">
                          <p className="text-[10px] text-[hsl(var(--muted-foreground))]">
                            {editForm.role !== u.role
                              ? "Role change resets permissions to the new role's defaults and signs the user out"
                              : editForm.email !== u.email
                                ? "Email change signs the user out — they log in with the new address"
                                : "Changes apply immediately"}
                          </p>
                          <div className="flex gap-1.5">
                            <button onClick={() => setEditUserId(null)} className="px-3 py-1.5 text-xs rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 hover:bg-[hsl(var(--muted))] transition-colors">Cancel</button>
                            <button
                              onClick={() => handleSaveUserEdit(u)}
                              disabled={editSaving || !editForm.name.trim() || !editForm.email.trim()}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-500 hover:bg-blue-600 text-white transition-colors disabled:opacity-50"
                              data-testid="edit-user-submit"
                            >
                              {editSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                              Save
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Inline password reset form */}
                    {isResetting && (
                      <div className="border border-amber-500/30 border-t-0 rounded-b-lg bg-amber-500/5 px-3 py-2 space-y-1.5" data-testid={`reset-form-${u.user_id}`}>
                        <p className="text-[10px] uppercase tracking-widest font-bold text-amber-600 mb-2">Reset Password — {u.name}</p>
                        <div className="grid grid-cols-3 gap-2">
                          {/* Secret key */}
                          <div className="relative">
                            <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold block mb-1">Secret Key</label>
                            <div className="relative">
                              <input
                                type={resetSecretShow ? "text" : "password"}
                                value={resetSecret}
                                onChange={e => setResetSecret(e.target.value)}
                                placeholder="••••"
                                className="w-full h-8 px-2.5 pr-7 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-amber-500 font-mono-data"
                                data-testid="reset-secret-input"
                              />
                              <button type="button" onClick={() => setResetSecretShow(v => !v)} className="absolute right-1.5 top-1.5 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
                                {resetSecretShow ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                              </button>
                            </div>
                          </div>
                          {/* New password */}
                          <div>
                            <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold block mb-1">New Password</label>
                            <div className="relative">
                              <input
                                type={resetPwdShow ? "text" : "password"}
                                value={resetNewPwd}
                                onChange={e => setResetNewPwd(e.target.value)}
                                placeholder="min 6 chars"
                                className="w-full h-8 px-2.5 pr-7 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-amber-500"
                                data-testid="reset-newpwd-input"
                              />
                              <button type="button" onClick={() => setResetPwdShow(v => !v)} className="absolute right-1.5 top-1.5 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
                                {resetPwdShow ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                              </button>
                            </div>
                          </div>
                          {/* Confirm */}
                          <div>
                            <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold block mb-1">Confirm</label>
                            <input
                              type="password"
                              value={resetConfirm}
                              onChange={e => setResetConfirm(e.target.value)}
                              placeholder="confirm"
                              className={`w-full h-8 px-2.5 text-sm rounded-md bg-[hsl(var(--muted))] border text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-amber-500 ${resetConfirm && resetConfirm !== resetNewPwd ? 'border-red-500' : 'border-[hsl(var(--border))]'}`}
                              data-testid="reset-confirm-input"
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-between pt-1">
                          <p className="text-[10px] text-[hsl(var(--muted-foreground))]">Enter the admin secret key to authorize this change</p>
                          <div className="flex gap-1.5">
                            <button onClick={() => setResetUserId(null)} className="px-3 py-1.5 text-xs rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 hover:bg-[hsl(var(--muted))] transition-colors">Cancel</button>
                            <button
                              onClick={handleResetPassword}
                              disabled={resetSaving || !resetSecret || !resetNewPwd || resetNewPwd !== resetConfirm}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-500 hover:bg-amber-600 text-white transition-colors disabled:opacity-50"
                              data-testid="reset-pwd-submit"
                            >
                              {resetSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <KeyRound className="w-3 h-3" />}
                              Reset
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
          </>)}
        </div>

        {/* Footer — the AI and Announcements tabs save with their own buttons */}
        <div className={`px-6 py-4 flex items-center justify-between border-t border-[hsl(var(--border))]/50 shrink-0 ${activeTab === "ai" || activeTab === "announcements" ? "hidden" : ""}`}>
          <button onClick={handleReset} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition-colors">
            <RotateCcw className="w-3.5 h-3.5" /> Revert changes
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold text-white transition-colors disabled:opacity-50"
            style={{ background: "linear-gradient(135deg, #f97316, #8b5cf6)" }}
            data-testid="settings-save-btn"
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Create Client sub-modal */}
    {showCreateClient && (
      <CreateClientModal
        onClose={() => setShowCreateClient(false)}
        onCreate={() => setShowCreateClient(false)}
      />
    )}

    {/* User Permissions Modal */}
    {permissionsUser && (
      <UserPermissionsModal
        user={permissionsUser}
        token={localStorage.getItem('rl_token')}
        onClose={() => setPermissionsUser(null)}
        onSaved={handlePermissionsSaved}
      />
    )}

    {/* Custom Digest Configure Modal */}
    {showCustomDigestModal && (
      <Dialog open onOpenChange={() => setShowCustomDigestModal(false)}>
        <DialogContent
          className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-lg p-0 overflow-hidden"
          data-testid="custom-digest-modal"
        >
          <div className="h-1 w-full bg-gradient-to-r from-orange-500 to-purple-500 shrink-0" />
          <div className="px-6 pt-5 pb-2">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(99,102,241,0.15)" }}>
                  <SlidersHorizontal className="w-4 h-4 text-orange-400" />
                </div>
                <div>
                  <p className="text-sm font-bold">Configure Custom Digest</p>
                  <p className="text-xs font-normal text-[hsl(var(--muted-foreground))]">Sent automatically at 12:00 PM IST alongside Owner Digest</p>
                </div>
              </DialogTitle>
            </DialogHeader>
          </div>

          <div className="px-6 py-3 space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Label */}
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] block mb-1.5">Digest Label</label>
              <input
                type="text"
                value={(form.custom_digest?.label) || "Custom Digest"}
                onChange={e => handleChange("custom_digest", { ...(form.custom_digest || {}), label: e.target.value })}
                placeholder="Custom Digest"
                className="w-full h-8 px-3 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                data-testid="custom-digest-label-input"
              />
            </div>

            {/* Show Financials Toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--muted))]/30 border border-[hsl(var(--border))]">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-emerald-500" />
                <div>
                  <p className="text-sm font-semibold">Show Financial Values</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Include USD pipeline values in the digest</p>
                </div>
              </div>
              <button
                onClick={() => handleChange("custom_digest", { ...(form.custom_digest || {}), show_financials: !(form.custom_digest?.show_financials ?? true) })}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${(form.custom_digest?.show_financials ?? true) ? 'bg-emerald-500/15 text-emerald-600 border border-emerald-500/30' : 'bg-[hsl(var(--muted))]/40 text-[hsl(var(--muted-foreground))] border border-[hsl(var(--border))]'}`}
                data-testid="custom-digest-financials-toggle"
              >
                {(form.custom_digest?.show_financials ?? true)
                  ? <><Eye className="w-3.5 h-3.5" /> Visible</>
                  : <><EyeOff className="w-3.5 h-3.5" /> Hidden</>
                }
              </button>
            </div>

            {/* Visible Stages */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Visible Stages</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleChange("custom_digest", { ...(form.custom_digest || {}), visible_stages: ["PO Received","Procurement","Manufacturing","Quality Control","Dispatch","Completed"] })}
                    className="text-[10px] text-orange-400 hover:text-orange-300 underline"
                    data-testid="custom-digest-select-all-stages"
                  >All</button>
                  <span className="text-[hsl(var(--muted-foreground))] text-[10px]">/</span>
                  <button
                    onClick={() => handleChange("custom_digest", { ...(form.custom_digest || {}), visible_stages: [] })}
                    className="text-[10px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] underline"
                    data-testid="custom-digest-deselect-all-stages"
                  >None</button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {["PO Received","Procurement","Manufacturing","Quality Control","Dispatch","Completed"].map(stage => {
                  const visStages = form.custom_digest?.visible_stages ?? ["PO Received","Procurement","Manufacturing","Quality Control","Dispatch","Completed"];
                  const isSelected = visStages.includes(stage);
                  const stageColors = { "PO Received": "#3b82f6", Procurement: "#f97316", Manufacturing: "#a855f7", "Quality Control": "#14b8a6", Dispatch: "#f59e0b", Completed: "#22c55e" };
                  const col = stageColors[stage] || "#f97316";
                  return (
                    <button
                      key={stage}
                      onClick={() => {
                        const cur = form.custom_digest?.visible_stages ?? ["PO Received","Procurement","Manufacturing","Quality Control","Dispatch","Completed"];
                        const next = isSelected ? cur.filter(s => s !== stage) : [...cur, stage];
                        handleChange("custom_digest", { ...(form.custom_digest || {}), visible_stages: next });
                      }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-semibold transition-all text-left`}
                      style={isSelected
                        ? { background: `${col}15`, color: col, borderColor: `${col}40` }
                        : { background: "transparent", color: "hsl(var(--muted-foreground))", borderColor: "hsl(var(--border))" }
                      }
                      data-testid={`custom-digest-stage-${stage.replace(/\s/g,'-').toLowerCase()}`}
                    >
                      <div className="w-2 h-2 rounded-full shrink-0" style={{ background: isSelected ? col : "#cbd5e1" }} />
                      {stage}
                      {isSelected && <Check className="w-3 h-3 ml-auto shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Recipients */}
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] block mb-2">Recipients</label>
              <div className="space-y-1.5 mb-2 max-h-28 overflow-y-auto">
                {((form.custom_digest?.recipients) || []).length === 0 && (
                  <p className="text-xs text-[hsl(var(--muted-foreground))] py-2 text-center">No recipients yet.</p>
                )}
                {((form.custom_digest?.recipients) || []).map((email, idx) => (
                  <div key={idx} className="flex items-center justify-between gap-2 p-2 rounded-md bg-[hsl(var(--muted))]/40 border border-[hsl(var(--border))]">
                    <span className="text-xs font-mono-data text-[hsl(var(--foreground))] truncate">{email}</span>
                    <button
                      onClick={() => handleChange("custom_digest", { ...(form.custom_digest || {}), recipients: (form.custom_digest?.recipients || []).filter((_, i) => i !== idx) })}
                      className="p-1 rounded hover:bg-red-500/10 text-[hsl(var(--muted-foreground))] hover:text-red-500 transition-colors shrink-0"
                      data-testid={`remove-custom-recipient-${idx}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={newCustomRecipient}
                  onChange={e => setNewCustomRecipient(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" && newCustomRecipient.includes("@")) {
                      handleChange("custom_digest", { ...(form.custom_digest || {}), recipients: [...(form.custom_digest?.recipients || []), newCustomRecipient.trim()] });
                      setNewCustomRecipient("");
                    }
                  }}
                  placeholder="recipient@company.com"
                  className="flex-1 h-8 px-3 text-xs rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="new-custom-recipient-input"
                />
                <button
                  onClick={() => {
                    const e = newCustomRecipient.trim();
                    if (e && e.includes("@")) {
                      handleChange("custom_digest", { ...(form.custom_digest || {}), recipients: [...(form.custom_digest?.recipients || []), e] });
                      setNewCustomRecipient("");
                    }
                  }}
                  disabled={!newCustomRecipient.includes("@")}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50 transition-colors"
                  data-testid="add-custom-recipient-btn"
                >
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
            </div>

            {/* Test Send */}
            <div className="pt-2 border-t border-[hsl(var(--border))]/50">
              <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-2">Test Send</p>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={customDigestEmailAddr}
                  onChange={e => setCustomDigestEmailAddr(e.target.value)}
                  placeholder="test@company.com"
                  className="flex-1 h-8 px-3 text-xs rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="custom-digest-test-email-input"
                />
                <button
                  onClick={handleSendCustomDigest}
                  disabled={sendingCustomDigest || !customDigestEmailAddr}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50 transition-colors"
                  data-testid="send-custom-digest-btn"
                >
                  {sendingCustomDigest ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                  Send Preview
                </button>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-[hsl(var(--border))]/50 flex items-center justify-between">
            <p className="text-[10px] text-[hsl(var(--muted-foreground))]">Changes apply when you Save Settings.</p>
            <button
              onClick={() => setShowCustomDigestModal(false)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-orange-600 hover:bg-orange-700 text-white transition-colors"
              data-testid="custom-digest-done-btn"
            >
              Done
            </button>
          </div>
        </DialogContent>
      </Dialog>
    )}

    {/* Digest Recipients Dialog */}
    {showRecipientsDialog && (
      <Dialog open onOpenChange={() => setShowRecipientsDialog(false)}>
        <DialogContent
          className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md p-0 overflow-hidden"
          data-testid="digest-recipients-dialog"
        >
          <div className="h-1 w-full bg-gradient-to-r from-purple-500 to-orange-500 shrink-0" />
          <div className="px-6 pt-5 pb-2">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(168,85,247,0.15)" }}>
                  <Calendar className="w-4 h-4 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm font-bold">Owner Digest Recipients</p>
                  <p className="text-xs font-normal text-[hsl(var(--muted-foreground))]">These addresses receive the full 12pm IST pipeline report</p>
                </div>
              </DialogTitle>
            </DialogHeader>
          </div>
          <div className="px-6 py-2 space-y-2 max-h-64 overflow-y-auto">
            {(form.digest_management_emails || []).length === 0 && (
              <p className="text-xs text-[hsl(var(--muted-foreground))] py-3 text-center">No recipients yet. Add management email addresses below.</p>
            )}
            {(form.digest_management_emails || []).map((email, idx) => (
              <div key={idx} className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-[hsl(var(--muted))]/40 border border-[hsl(var(--border))]">
                <span className="text-sm font-mono-data text-[hsl(var(--foreground))] truncate">{email}</span>
                <button
                  onClick={() => handleChange("digest_management_emails", (form.digest_management_emails || []).filter((_, i) => i !== idx))}
                  className="p-1 rounded hover:bg-red-500/10 text-[hsl(var(--muted-foreground))] hover:text-red-500 transition-colors shrink-0"
                  data-testid={`remove-digest-recipient-${idx}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="px-6 py-4 border-t border-[hsl(var(--border))]/50">
            <div className="flex gap-2">
              <input
                type="email"
                value={newRecipientEmail}
                onChange={e => setNewRecipientEmail(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && newRecipientEmail.includes("@")) {
                    handleChange("digest_management_emails", [...(form.digest_management_emails || []), newRecipientEmail.trim()]);
                    setNewRecipientEmail("");
                  }
                }}
                placeholder="management@company.com"
                className="flex-1 h-9 px-3 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="new-digest-recipient-input"
              />
              <button
                onClick={() => {
                  const e = newRecipientEmail.trim();
                  if (e && e.includes("@")) {
                    handleChange("digest_management_emails", [...(form.digest_management_emails || []), e]);
                    setNewRecipientEmail("");
                  }
                }}
                disabled={!newRecipientEmail.includes("@")}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-purple-600 hover:bg-purple-700 text-white transition-colors disabled:opacity-50"
                data-testid="add-digest-recipient-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </div>
            <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-2">Changes saved when you click Save Settings in the main panel.</p>
          </div>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
}
